import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { journeyPublicationApi } from '../../api/journeyPublicationApi';
import { useTravelText } from '../../app/TravelLocaleContext';
import {
  blockingReasonCopy,
  buildRefreshRequest,
  emptyPublication,
  isPublicationAbsentError,
  normalizeJourneyPublication,
  orchestrateMakePublic,
  ownerPublicationActions,
  publicationErrorKind,
  publicationErrorDescriptor,
  publicationRefreshPresentation,
  publicationStatusKey,
  publicationVisibilityKeys,
  publicJourneyPath,
  refreshErrorPresentation,
} from './journeyPublicationModel';
import JourneyStoryCurationPanel from './JourneyStoryCurationPanel';
import './journey-publication.css';

export default function JourneyPublicationSection({
  journeyId,
  requesterRole,
  lifecycleStatus,
  journeyRowVersion = null,
  itineraryRowVersion = null,
  disabled = false,
}) {
  // 未包 Provider 時 useTravelText 回退預設語系（zh_TW），與既有 publicationRefresh 回退一致
  const { locale, t, tf } = useTravelText();
  const generation = useRef(0);
  const disposed = useRef(false);
  const busyRef = useRef(false);
  const openerRef = useRef(null);
  const dialogRef = useRef(null);
  const [publication, setPublication] = useState(() => emptyPublication(journeyId));
  const [phase, setPhase] = useState('loading');
  // 錯誤／回饋存 key 或後端原文，呈現時翻譯，切換語系即更新
  const [error, setError] = useState(null);
  const [feedback, setFeedback] = useState('');
  const [refreshFailure, setRefreshFailure] = useState('');
  const [pending, setPending] = useState(false);
  const [confirmKind, setConfirmKind] = useState(null);
  const [curationOpen, setCurationOpen] = useState(false);
  const [storyRevision, setStoryRevision] = useState(null);
  const [curationReloadToken, setCurationReloadToken] = useState(0);
  const titleId = useId();
  const confirmTitleId = useId();
  const curationPanelId = useId();

  const load = useCallback(async ({ soft = false } = {}) => {
    const requestId = ++generation.current;
    if (!soft) {
      setPhase('loading');
      setError(null);
    }
    try {
      const payload = await journeyPublicationApi.getByJourney(journeyId);
      if (requestId !== generation.current) return;
      setPublication(normalizeJourneyPublication(payload, journeyId));
      setPhase('ready');
    } catch (cause) {
      if (requestId !== generation.current) return;
      if (isPublicationAbsentError(cause)) {
        setPublication(emptyPublication(journeyId));
        setPhase('ready');
        return;
      }
      const kind = publicationErrorKind(cause);
      setError(publicationErrorDescriptor(kind, cause));
      setPhase('error');
    }
  }, [journeyId]);

  useEffect(() => {
    disposed.current = false;
    load();
    return () => {
      disposed.current = true;
      generation.current += 1;
    };
  }, [load]);

  useEffect(() => {
    if (!confirmKind) return undefined;
    const node = dialogRef.current;
    const focusable = () => [...(node?.querySelectorAll(
      'button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])',
    ) || [])];
    focusable()[0]?.focus();
    function onKey(event) {
      if (event.key === 'Escape' && !busyRef.current) {
        event.preventDefault();
        setConfirmKind(null);
        return;
      }
      if (event.key !== 'Tab') return;
      const items = focusable();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
    document.addEventListener('keydown', onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
      openerRef.current?.focus?.();
    };
  }, [confirmKind]);

  const actions = ownerPublicationActions(publication, { requesterRole, lifecycleStatus });
  const publicPath = publicJourneyPath(publication);
  const confirmAction = confirmKind === 'UNPUBLISH' ? actions.secondary
    : (actions.primary && actions.primary.kind === confirmKind ? actions.primary : null);

  function openConfirm(kind, event) {
    if (!kind || busyRef.current || disabled || pending) return;
    openerRef.current = event?.currentTarget || document.activeElement;
    setError(null);
    setFeedback('');
    setConfirmKind(kind);
  }

  function closeConfirm() {
    if (busyRef.current) return;
    setConfirmKind(null);
  }

  async function applyServerPublication(payload) {
    setPublication(normalizeJourneyPublication(payload, journeyId));
    setPhase('ready');
  }

  async function reloadAfterFailure(kind) {
    try {
      const payload = await journeyPublicationApi.getByJourney(journeyId);
      if (disposed.current) return;
      setPublication(normalizeJourneyPublication(payload, journeyId));
      setPhase('ready');
    } catch (reloadCause) {
      if (disposed.current) return;
      if (isPublicationAbsentError(reloadCause)) {
        setPublication(emptyPublication(journeyId));
        setPhase('ready');
        return;
      }
      if (!error) setError(publicationErrorDescriptor(kind, reloadCause));
    }
  }

  async function submitConfirm() {
    if (!confirmKind || busyRef.current || disabled) return;
    const requestId = generation.current;
    busyRef.current = true;
    setPending(true);
    setError(null);
    try {
      if (confirmKind === 'MAKE_PUBLIC') {
        const result = await orchestrateMakePublic({
          journeyId,
          current: publication,
          createDraft: journeyPublicationApi.createDraft,
          changeVisibility: journeyPublicationApi.changeVisibility,
          publish: journeyPublicationApi.publish,
          getByJourney: journeyPublicationApi.getByJourney,
          shouldAbort: () => requestId !== generation.current || disposed.current,
        });
        if (requestId !== generation.current) return;
        setConfirmKind(null);
        if (!result.aborted) await applyServerPublication(result.publication);
        return;
      }

      if (confirmKind === 'SET_PUBLIC') {
        const payload = await journeyPublicationApi.changeVisibility(journeyId, {
          visibility: 'PUBLIC',
          expectedRowVersion: publication.rowVersion,
          confirm: true,
        });
        if (requestId !== generation.current) return;
        setConfirmKind(null);
        await applyServerPublication(payload);
        return;
      }

      if (confirmKind === 'UNPUBLISH') {
        const payload = await journeyPublicationApi.unpublish(journeyId, {
          expectedRowVersion: publication.rowVersion,
          confirm: true,
        });
        if (requestId !== generation.current) return;
        setConfirmKind(null);
        await applyServerPublication(payload);
      }
    } catch (cause) {
      if (requestId !== generation.current) return;
      const kind = publicationErrorKind(cause);
      setConfirmKind(null);
      setError(publicationErrorDescriptor(kind, cause));
      if (kind === 'conflict' || kind === 'already-exists' || kind === 'invalid-lifecycle'
        || kind === 'lifecycle-not-writable' || kind === 'ownership-drift') {
        await reloadAfterFailure(kind);
      } else if (kind !== 'forbidden' && kind !== 'unauthorized' && kind !== 'validation') {
        await reloadAfterFailure(kind);
      } else if (kind === 'forbidden' || kind === 'validation') {
        await reloadAfterFailure(kind);
      }
    } finally {
      busyRef.current = false;
      if (!disposed.current) setPending(false);
    }
  }

  async function submitRefresh() {
    if (busyRef.current || disabled || confirmKind !== 'REFRESH') return;
    const requestId = ++generation.current;
    const body = buildRefreshRequest({
      journeyRowVersion,
      itineraryRowVersion,
      publicationRowVersion: publication.rowVersion,
      storyRevision: curationOpen ? storyRevision : null,
    });
    if (!body.expectedJourneyRowVersion || !body.expectedItineraryRowVersion || !body.expectedPublicationRowVersion) {
      setError({ key: 'travel.publication.refresh.versionsMissing' });
      setConfirmKind(null);
      await load({ soft: true });
      return;
    }
    busyRef.current = true;
    setPending(true);
    setError(null);
    setFeedback('');
    try {
      const payload = await journeyPublicationApi.refresh(journeyId, body);
      if (requestId !== generation.current || disposed.current || String(payload?.journeyId || journeyId) !== String(journeyId)) {
        return;
      }
      const normalized = normalizeJourneyPublication(payload, journeyId);
      if (normalized.journeyId && String(normalized.journeyId) !== String(journeyId)) return;
      setPublication(normalized);
      setPhase('ready');
      setConfirmKind(null);
      setFeedback(normalized.noOp
        ? 'travel.publication.refresh.successNoOp'
        : 'travel.publication.refresh.successChanged');
      setRefreshFailure('');
      const reloaded = await journeyPublicationApi.getByJourney(journeyId);
      if (requestId !== generation.current || disposed.current) return;
      const next = normalizeJourneyPublication(reloaded, journeyId);
      if (!next.journeyId || String(next.journeyId) === String(journeyId)) setPublication(next);
    } catch (cause) {
      if (requestId !== generation.current || disposed.current) return;
      const presented = refreshErrorPresentation(cause, locale);
      setConfirmKind(null);
      setError({ key: presented.messageKey });
      setRefreshFailure(presented.kind);
      if (presented.reload) {
        setCurationReloadToken((token) => token + 1);
        await reloadAfterFailure('conflict');
      }
    } finally {
      busyRef.current = false;
      if (!disposed.current) setPending(false);
    }
  }

  const refreshState = publicationRefreshPresentation(publication);
  const refreshBlocked = refreshState === 'PUBLISHED_CHANGED_BLOCKED'
    || (publication.status === 'PUBLISHED' && publication.refreshAllowed === false && refreshState !== 'PUBLISHED_UNCHANGED');
  const showRefresh = actions.owner && !refreshBlocked && (
    refreshState === 'PUBLISHED_CHANGED' || refreshState === 'PUBLISHED_UNKNOWN'
  );
  const showBlockedRefresh = actions.owner && refreshBlocked && refreshState !== 'UNPUBLISHED';
  const refreshLabel = refreshState === 'PUBLISHED_UNKNOWN'
    ? t('travel.publication.refresh.ctaUnknown')
    : t('travel.publication.refresh.cta');
  const errorText = error ? (error.key ? t(error.key) : error.text) : '';
  const visibilityKeys = publicationVisibilityKeys(publication);
  const confirmKeys = confirmAction?.keys || null;
  const showCuration = phase === 'ready' && actions.owner
    && publication.presence === 'PRESENT' && publication.status !== 'REMOVED';

  return (
    <section className="journey-publication" id="journey-publication" tabIndex={-1} aria-labelledby={titleId}>
      <h3 id={titleId}>{t('travel.publication.section.title')}</h3>
      {phase === 'loading' ? (
        <p className="journey-publication__pending" aria-live="polite">{t('travel.publication.section.loading')}</p>
      ) : null}
      {phase === 'ready' ? (
        <>
          <p
            className="journey-publication__status"
            aria-live="polite"
            data-publication-status={
              publication.presence === 'NO_PUBLICATION'
                ? 'NO_PUBLICATION'
                : (publication.status || 'UNKNOWN')
            }
          >
            {t(publicationStatusKey(publication))}
          </p>
          <p className="journey-publication__visibility">
            {visibilityKeys.labelKey
              ? tf(visibilityKeys.key, { label: t(visibilityKeys.labelKey) })
              : t(visibilityKeys.key)}
          </p>
          {actions.cancelledHintKey ? (
            <p className="journey-publication__hint">{t(actions.cancelledHintKey)}</p>
          ) : null}
          {publication.status === 'PUBLISHED' && refreshState === 'PUBLISHED_CHANGED' ? (
            <p className="journey-publication__hint" data-refresh-state="PUBLISHED_CHANGED">
              {t('travel.publication.refresh.changed')}
              {' '}
              {t('travel.publication.refresh.changedHint')}
            </p>
          ) : null}
          {publication.status === 'PUBLISHED' && refreshState === 'PUBLISHED_UNCHANGED' ? (
            <p className="journey-publication__hint" data-refresh-state="PUBLISHED_UNCHANGED">
              {t('travel.publication.refresh.unchanged')}
            </p>
          ) : null}
          {publication.status === 'PUBLISHED' && refreshState === 'PUBLISHED_UNKNOWN' ? (
            <p className="journey-publication__hint" data-refresh-state="PUBLISHED_UNKNOWN">
              {t('travel.publication.refresh.unknown')}
            </p>
          ) : null}
          {refreshState === 'PUBLISHED_CHANGED_BLOCKED' ? (
            <div data-refresh-state="PUBLISHED_CHANGED_BLOCKED">
              <p className="journey-publication__hint">{t('travel.publication.refresh.blocked')}</p>
              <ul className="journey-publication__reasons">
                {(publication.blockingReasons.length ? publication.blockingReasons : ['UNKNOWN']).map((reason) => (
                  <li key={reason}>{blockingReasonCopy(reason, locale)}</li>
                ))}
              </ul>
              <p className="journey-publication__hint">
                <Link to={`/journeys/${journeyId}/planning`}>{t('travel.publication.refresh.manage')}</Link>
              </p>
            </div>
          ) : null}
        </>
      ) : null}
      {errorText ? <p className="journey-publication__error" role="alert">{errorText}</p> : null}
      {refreshFailure === 'stale' || refreshFailure === 'generic' || refreshFailure === 'concurrent' ? (
        <div className="journey-publication__actions">
          <button type="button" onClick={() => { setRefreshFailure(''); load(); }}>
            {refreshFailure === 'generic' ? t('travel.publication.refresh.retry') : t('travel.publication.refresh.reload')}
          </button>
        </div>
      ) : null}
      {refreshFailure === 'day' || refreshFailure === 'custom-location' || refreshFailure === 'eligibility' ? (
        <p className="journey-publication__hint">
          <Link to={`/journeys/${journeyId}/planning`}>{t('travel.publication.refresh.manage')}</Link>
        </p>
      ) : null}
      {phase === 'error' ? (
        <div className="journey-publication__actions">
          <button type="button" onClick={() => load()}>{t('travel.publication.section.reload')}</button>
        </div>
      ) : null}
      {feedback ? <p className="journey-publication__status" role="status">{t(feedback)}</p> : null}

      <div className="journey-publication__actions">
        {publicPath ? (
          <Link className="journey-publication__link" to={publicPath}>
            {t('travel.publication.refresh.view')}
          </Link>
        ) : null}
        {publication.status === 'PUBLISHED' && publication.publicationId && !publicPath ? (
          <p className="journey-publication__hint">{t('travel.publication.refresh.viewUnavailable')}</p>
        ) : null}
        {showRefresh ? (
          <button
            type="button"
            className="journey-publication__primary"
            data-testid="publication-refresh-cta"
            disabled={pending || disabled}
            onClick={(event) => openConfirm('REFRESH', event)}
          >
            {refreshLabel}
          </button>
        ) : null}
        {showBlockedRefresh ? (
          <button type="button" className="journey-publication__primary" data-testid="publication-refresh-cta" disabled>
            {refreshLabel}
          </button>
        ) : null}
        {publication.status === 'PUBLISHED' && publication.publicationId ? (
          <Link className="journey-publication__link" to={`/group-trips/new?publicationId=${publication.publicationId}`}>
            {t('travel.publicJourney.createGroupTrip')}
          </Link>
        ) : null}
        {actions.primary ? (
          <button
            type="button"
            className="journey-publication__primary"
            disabled={pending || disabled}
            onClick={() => openConfirm(actions.primary.kind)}
          >
            {t(actions.primary.keys.label)}
          </button>
        ) : null}
      </div>
      {actions.secondary ? (
        <div className="journey-publication__secondary">
          <button
            type="button"
            className="journey-publication__danger"
            disabled={pending || disabled}
            onClick={() => openConfirm(actions.secondary.kind)}
          >
            {t(actions.secondary.keys.label)}
          </button>
        </div>
      ) : null}

      {showCuration ? (
        <div className="journey-publication__secondary">
          <button
            type="button"
            data-testid="journey-story-toggle"
            aria-expanded={curationOpen}
            aria-controls={curationPanelId}
            onClick={() => setCurationOpen((open) => !open)}
          >
            {t(curationOpen ? 'travel.publication.story.curation.close' : 'travel.publication.story.curation.heading')}
          </button>
        </div>
      ) : null}
      {showCuration && curationOpen ? (
        <div id={curationPanelId}>
          <JourneyStoryCurationPanel
            journeyId={journeyId}
            ownerMemberId={publication.ownerMemberId}
            reloadToken={curationReloadToken}
            disabled={pending || disabled}
            onRevisionChange={setStoryRevision}
            onStoryChanged={() => load({ soft: true })}
          />
        </div>
      ) : null}

      {confirmKind && confirmAction ? (
        <div className="journey-publication-confirm" role="presentation">
          <button
            type="button"
            className="journey-publication-confirm__backdrop"
            aria-label={t('travel.journeyWorkspace.lifecycle.closeConfirm')}
            disabled={pending}
            onClick={closeConfirm}
          />
          <div
            className="journey-publication-confirm__dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby={confirmTitleId}
            ref={dialogRef}
          >
            <h3 id={confirmTitleId}>{t(confirmKeys.title)}</h3>
            <p>{t(confirmKeys.body)}</p>
            {errorText ? <p className="journey-publication__error" role="alert">{errorText}</p> : null}
            <div className="journey-publication-confirm__actions">
              <button type="button" disabled={pending} onClick={closeConfirm}>{t('travel.journeyWorkspace.lifecycle.back')}</button>
              <button
                type="button"
                className={confirmKind === 'UNPUBLISH' ? 'journey-publication__danger' : 'journey-publication__primary'}
                disabled={pending}
                onClick={submitConfirm}
              >
                {pending ? t('travel.journeyWorkspace.updating') : t(confirmKeys.confirmAction)}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {confirmKind === 'REFRESH' ? (
        <div className="journey-publication-confirm" role="presentation">
          <button
            type="button"
            className="journey-publication-confirm__backdrop"
            aria-label={t('travel.publication.refresh.cancel')}
            disabled={pending}
            onClick={closeConfirm}
          />
          <div
            className="journey-publication-confirm__dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby={confirmTitleId}
            ref={dialogRef}
          >
            <h3 id={confirmTitleId}>{t('travel.publication.refresh.dialogTitle')}</h3>
            <ul className="journey-publication__reasons">
              <li>{t('travel.publication.refresh.dialogPrevious')}</li>
              <li>{t('travel.publication.refresh.dialogNext')}</li>
              <li>{t('travel.publication.refresh.dialogLink')}</li>
              <li>{t('travel.publication.refresh.dialogCopy')}</li>
              <li>{t('travel.publication.refresh.dialogFailure')}</li>
            </ul>
            {errorText ? <p className="journey-publication__error" role="alert">{errorText}</p> : null}
            {pending ? <p className="journey-publication__pending" role="status">{t('travel.publication.refresh.pending')}</p> : null}
            <div className="journey-publication-confirm__actions">
              <button type="button" disabled={pending} onClick={closeConfirm}>
                {t('travel.publication.refresh.cancel')}
              </button>
              <button
                type="button"
                className="journey-publication__primary"
                disabled={pending}
                onClick={submitRefresh}
              >
                {pending ? t('travel.publication.refresh.pending') : t('travel.publication.refresh.cta')}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}
